/**
 * moduleBoardPlan — the projection plan for a MODULE PAGE's board: one module
 * shown with its internals and every incoming/outgoing connection as a boundary
 * port stub.
 *
 * It is a synthesized, in-memory `view` — never authored, never saved. The source
 * is `show <Module>` + `show in <Module>`, so the shown set is the module plus its
 * whole subtree (a container renders its internals; a leaf renders alone).
 * The engine's `planView` then AUTO-DERIVES the boundary stubs for every edge that
 * crosses the shown set — the exact VIEWS §7 machinery the `view` DSL uses, so the
 * module's calls-out / called-by peers surface as `out` / `in` port nubs on the
 * board with no bespoke edge walking here. The resulting plan feeds a store-free
 * {@link ArchitectureView} (the `ViewRenderer` board-representation mount), so the
 * page's board is the REAL board, solved once with this projection applied.
 *
 * `evalRefOrType([Module])` resolves a module ref to the single node (no
 * descendants); the `in <Module>` predicate resolves to the strict descendant set
 * — hence the two clauses. Pure + engine-only so the plan is unit-testable without
 * a DOM.
 */
import {
  parseViewSource,
  type ViewDeclV2,
  type ResolvedModel,
  type DependencyGraph,
} from "@archlang/engine";
import type { Vfs } from "@archlang/viewer-core";
import type { ViewProjectionPlan } from "@archlang/scene/arch-model";
import { collectViewGovernance, computeViewPlan } from "./viewPlan.js";

/** The placeholder name the synthesized module-page view compiles under (never
 *  shown to the user, never registered in the model). */
export const MODULE_BOARD_VIEW_NAME = "ModuleBoard";

/**
 * The in-memory `view` that shows one module + its subtree. `qualKey` is the
 * module's qualified dotted key (the same key edges reference and the index opens
 * with). Returns null on a blank key or a parse failure (defensive — the source
 * is fixed, so a failure means an unrepresentable key).
 */
export function moduleBoardView(qualKey: string): ViewDeclV2 | null {
  if (!qualKey) return null;
  const src = `view ${MODULE_BOARD_VIEW_NAME} {\n    show ${qualKey}\n    show in ${qualKey}\n}`;
  const r = parseViewSource(src, "__module_board__.arch");
  return r.errors.length > 0 ? null : r.decl;
}

/**
 * The board projection plan for a module page: the {@link moduleBoardView} draft
 * run through the full view pipeline (governance closure wired so a `style
 * violating` in scope still resolves, though the synthesized view has none). Null
 * when the model/graph is not ready or the key is unrepresentable — the page then
 * renders no board (its detail rail still stands).
 */
export function computeModuleBoardPlan(
  resolvedModel: ResolvedModel | null,
  graph: DependencyGraph | null,
  sources: Vfs | null,
  qualKey: string,
): ViewProjectionPlan | null {
  const draft = moduleBoardView(qualKey);
  if (!resolvedModel || !graph || !draft) return null;
  const governance = collectViewGovernance(resolvedModel, sources);
  return computeViewPlan(resolvedModel, graph, null, null, {}, draft, governance);
}
